import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  RiMore2Line,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { JobProduct } from '@/api/rest/resources/types/jobProducts';
import { PlotPortfolioProduct } from '@/api/rest/resources/types/plotPortfolioProducts';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useJobProducts } from '@/hooks/useJobProducts';
import { usePlotPortfolioProducts } from '@/hooks/usePlotPortfolioProducts';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import {
  StaffPortalTableCell,
  StaffPortalTableEmptyRow,
  StaffPortalTableHeadCell,
} from '@/pages/shared/staff-portal/components/StaffPortalTable';

import { DeleteJobProductDialog } from './components/DeleteJobProductDialog';
import { DeletePlotPortfolioProductDialog } from './components/DeletePlotPortfolioProductDialog';
import { JobProductFormDialog } from './components/JobProductFormDialog';
import { PlotPortfolioProductFormDialog } from './components/PlotPortfolioProductFormDialog';

export const NdiProductsPage = () => {
  const { t } = useTranslation();
  const { data: jobProductsData } = useJobProducts();
  const jobProducts = jobProductsData.results;
  const { data: plotPortfolioProductsData } = usePlotPortfolioProducts();
  const plotPortfolioProducts = plotPortfolioProductsData.results;

  const [showAddJobProductDialog, setShowAddJobProductDialog] = useState(false);
  const [editJobProduct, setEditJobProduct] = useState<JobProduct | undefined>(undefined);
  const [deleteJobProduct, setDeleteJobProduct] = useState<JobProduct | null>(null);

  const [showAddPlotPortfolioProductDialog, setShowAddPlotPortfolioProductDialog] = useState(false);
  const [editPlotPortfolioProduct, setEditPlotPortfolioProduct] = useState<PlotPortfolioProduct | undefined>(undefined);
  const [deletePlotPortfolioProduct, setDeletePlotPortfolioProduct] = useState<PlotPortfolioProduct | null>(null);

  return (
    <>
      <SignedInPageLayout
        items={[]}
        withCustomContentContainer
        showBreadcrumbs={false}
        navBarProps={{
          title: t('shared.ndiProducts.title'),
          showSearchBar: false,
          showFilters: false,
          children: (
            <Dropdown modal={false}>
              <DropdownTrigger data-testid='add-product-menu-trigger' asChild>
                <Button leftAdornment={<Icon icon='ri-add-line' />}>{t('shared.ndiProducts.addButton')}</Button>
              </DropdownTrigger>
              <DropdownContent side='bottom' sideOffset={10}>
                <DropdownItem
                  data-testid='add-product-menu-job-product'
                  onClick={() => setShowAddJobProductDialog(true)}
                >
                  {t('shared.ndiProducts.addMenu.jobProduct')}
                </DropdownItem>
                <DropdownItem
                  data-testid='add-product-menu-plot-portfolio-product'
                  onClick={() => setShowAddPlotPortfolioProductDialog(true)}
                >
                  {t('shared.ndiProducts.addMenu.plotPortfolioProduct')}
                </DropdownItem>
              </DropdownContent>
            </Dropdown>
          ),
        }}
      >
        <Stack className='px-48 py-12 gap-9'>
          <Stack spacing={4}>
            <span className='typography-h4'>{t('shared.ndiProducts.sections.jobProducts')}</span>
            <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.displayName')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.productName')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.initialSetupCostCredits')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.jobCreationCostCredits')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.jobExecutionCostCredits')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.subscriptionsCount')}
                    </StaffPortalTableHeadCell>
                    <TableHead className='w-px' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobProducts.length === 0 && (
                    <StaffPortalTableEmptyRow colSpan={7} message={t('shared.ndiProducts.emptyState')} />
                  )}
                  {jobProducts.map((jobProduct) => (
                    <TableRow key={jobProduct.id} className='border-b border-divider'>
                      <StaffPortalTableCell className='font-medium'>{jobProduct.display_name}</StaffPortalTableCell>
                      <StaffPortalTableCell>{jobProduct.name}</StaffPortalTableCell>
                      <StaffPortalTableCell>{jobProduct.initial_setup_cost_credits}</StaffPortalTableCell>
                      <StaffPortalTableCell>{jobProduct.job_creation_cost_credits}</StaffPortalTableCell>
                      <StaffPortalTableCell>{jobProduct.job_execution_cost_credits}</StaffPortalTableCell>
                      <StaffPortalTableCell>{jobProduct.job_subscriptions_count}</StaffPortalTableCell>
                      <TableCell className='w-px py-3 pr-4 align-middle'>
                        <ProductRowActions
                          testIdPrefix='job-product'
                          onEdit={() => setEditJobProduct(jobProduct)}
                          onDelete={() => setDeleteJobProduct(jobProduct)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Stack>

          <Stack spacing={4}>
            <span className='typography-h4'>{t('shared.ndiProducts.sections.plotPortfolioProducts')}</span>
            <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.displayName')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.name')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.initialSetupCostCredits')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.plotCreationCostCredits')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.maxPlots')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.maxHectares')}</StaffPortalTableHeadCell>
                    <TableHead className='w-px' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plotPortfolioProducts.length === 0 && (
                    <StaffPortalTableEmptyRow colSpan={7} message={t('shared.ndiProducts.emptyState')} />
                  )}
                  {plotPortfolioProducts.map((plotPortfolioProduct) => (
                    <TableRow key={plotPortfolioProduct.id} className='border-b border-divider'>
                      <StaffPortalTableCell className='font-medium'>
                        {plotPortfolioProduct.display_name}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>{plotPortfolioProduct.name}</StaffPortalTableCell>
                      <StaffPortalTableCell>{plotPortfolioProduct.initial_setup_cost_credits}</StaffPortalTableCell>
                      <StaffPortalTableCell>{plotPortfolioProduct.plot_creation_cost_credits}</StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {plotPortfolioProduct.max_plots ?? t('shared.ndiProducts.noLimit')}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {plotPortfolioProduct.max_hectares ?? t('shared.ndiProducts.noLimit')}
                      </StaffPortalTableCell>
                      <TableCell className='w-px py-3 pr-4 align-middle'>
                        <ProductRowActions
                          testIdPrefix='plot-portfolio-product'
                          onEdit={() => setEditPlotPortfolioProduct(plotPortfolioProduct)}
                          onDelete={() => setDeletePlotPortfolioProduct(plotPortfolioProduct)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Stack>
        </Stack>
      </SignedInPageLayout>
      <JobProductFormDialog open={showAddJobProductDialog} onClose={() => setShowAddJobProductDialog(false)} />
      <JobProductFormDialog
        open={!!editJobProduct}
        onClose={() => setEditJobProduct(undefined)}
        editItem={editJobProduct}
      />
      <DeleteJobProductDialog jobProduct={deleteJobProduct} onClose={() => setDeleteJobProduct(null)} />

      <PlotPortfolioProductFormDialog
        open={showAddPlotPortfolioProductDialog}
        onClose={() => setShowAddPlotPortfolioProductDialog(false)}
      />
      <PlotPortfolioProductFormDialog
        open={!!editPlotPortfolioProduct}
        onClose={() => setEditPlotPortfolioProduct(undefined)}
        editItem={editPlotPortfolioProduct}
      />
      <DeletePlotPortfolioProductDialog
        plotPortfolioProduct={deletePlotPortfolioProduct}
        onClose={() => setDeletePlotPortfolioProduct(null)}
      />
    </>
  );
};

const ProductRowActions = ({
  testIdPrefix,
  onEdit,
  onDelete,
}: {
  testIdPrefix: string;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dropdown modal={false}>
      <DropdownTrigger data-testid={`${testIdPrefix}-row-overflow-menu`}>
        <div className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary-100 text-primary-100 hover:bg-neutral-black-4 cursor-pointer'>
          <RiMore2Line size={24} />
        </div>
      </DropdownTrigger>
      <DropdownContent side='bottom' sideOffset={10}>
        <DropdownItem
          data-testid={`${testIdPrefix}-action-edit`}
          onClick={onEdit}
          leftAdornment={<Icon icon='ri-edit-line' />}
        >
          {t('global.ui.buttons.edit')}
        </DropdownItem>
        <DropdownItem
          data-testid={`${testIdPrefix}-action-delete`}
          onClick={onDelete}
          leftAdornment={<Icon icon='ri-delete-bin-line' />}
        >
          {t('global.ui.buttons.delete')}
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
};
